import styles from './index.module.css'

import { format } from 'date-fns'
import React from 'react'
import { useLocation } from 'react-router-dom'

import mixpanelClient from '@disco/api/mixpanel'
import { updateUserSettings } from '@disco/state-management/actions/UserActions'
import { getUser } from '@disco/state-management/selectors/user'

import CalendarSvg from 'assets/icons/calendar.svg'
import CloseSvg from 'assets/icons/close.svg'

import { Box, Button, Flex, IconButton, Link, Text } from 'components/ds'

import { useDispatch, useSelector } from 'hooks/use-redux'

export function SchoolOfDiscoBanner({
  nextSessionDate,
  nextSessionUrl,
}: {
  nextSessionDate: Date
  nextSessionUrl: string
}) {
  const user = useSelector(getUser)
  const dispatch = useDispatch()
  const location = useLocation()

  function handleClickConfirm() {
    updateUserSettingsSchoolOfDiscoBanner()
    mixpanelClient.sendPureEvent('Click button', {
      Copy: 'Confirm',
      Type: 'School of DISCO Banner',
      Context: 'Confirm',
      Class: 'Fundamentals',
      Location: 'Explorer',
      Url: location.pathname,
      Destination: nextSessionUrl,
      'Next session date': nextSessionDate,
    })
  }

  function handleClickPickAnotherTime() {
    updateUserSettingsSchoolOfDiscoBanner()
    mixpanelClient.sendPureEvent('Click button', {
      Copy: 'Pick another time',
      Type: 'School of DISCO Banner',
      Context: 'Pick another time',
      Class: 'Fundamentals',
      Location: 'Explorer',
      Url: location.pathname,
      Destination: nextSessionUrl,
      'Next session date': nextSessionDate,
    })
  }

  function handleClickClose() {
    dispatch(
      updateUserSettings({
        settings: {
          school_of_disco_banner_dismissed_basic_skills_class_at: new Date(),
        },
      }),
    )
    mixpanelClient.sendPureEvent('Click button', {
      Type: 'School of DISCO Banner',
      Context: 'Dismiss',
      Class: 'Fundamentals',
      Location: 'Explorer',
      Url: location.pathname,
    })
  }

  function updateUserSettingsSchoolOfDiscoBanner() {
    dispatch(
      updateUserSettings({
        settings: { school_of_disco_banner_confirmed_basic_skills_class: true },
      }),
    )
  }

  const urlParams = new URLSearchParams({
    name:
      user.first_name && user.last_name
        ? `${user.first_name} ${user.last_name}`
        : user.first_name,
    email: user.email,
  }).toString()

  return (
    <Flex
      className={styles['school-of-disco-banner']}
      align="center"
      height="52px"
      pl="4"
      pr="3"
    >
      <Flex mr="2">
        <CalendarSvg width="20px" height="20px" />
      </Flex>
      <Box display="inline-block">
        <Text weight="bold" size="2">
          You’re Invited!{' '}
        </Text>
        <Flex display="inline-flex">
          <Box>
            <Text size="2">Join us </Text>
            <Text weight="bold" size="2">
              {format(nextSessionDate, "EEEE, LLL d 'at' h:mmaaa")}{' '}
            </Text>
            <Text
              size="2"
              className={styles['school-of-disco-banner--text-guidance']}
            >
              for personalized guidance to master DISCO 3x faster.
            </Text>
          </Box>
        </Flex>
      </Box>
      <Flex flexGrow="1" justify="end" align="center">
        <Link
          target="_blank"
          className={styles['school-of-disco-banner-link']}
          href={`${nextSessionUrl}?${urlParams}`}
          onClick={handleClickConfirm}
        >
          <Button mr="2">Confirm {format(nextSessionDate, 'LLL d')}</Button>
        </Link>
        <Link
          target="_blank"
          className={styles['school-of-disco-banner-link']}
          href={`https://calendly.com/school-of-disco/disco-fundamentals-class?${urlParams}`}
          onClick={handleClickPickAnotherTime}
        >
          <Button variant="outline">Pick another time</Button>
        </Link>
        <IconButton variant="ghost" size="3" ml="3" onClick={handleClickClose}>
          <CloseSvg width="14px" height="14px" />
        </IconButton>
      </Flex>
    </Flex>
  )
}
